import React, { useMemo } from 'react';
import styled from 'styled-components';

// components
import CartSummaryMember from '../../../_molecules/CartSummaryMember';
import CreditCardIcon from '@icons/CreditCardIcon';
import PrintIcon from '@icons/PrintIcon';
import { BodyMedium, BodySmall, ButtonOutlineLarge, DividerLightGreyStyled, H4, LinkSmall } from '@styled';

// functionality
import { formatMoney } from '@general/helpers/currency.helpers';
import colors from '@general/colors';
import useCartItemsGroupedByMember from '@hooks/useCartItemsGroupedByMember';
import cartPropTypes from '@react/propTypes/cartPropTypes';

const PrintReceiptContainerStyled = styled.div`
    margin-top: 60px;

    @media print {
        display: none;
    }
`;

const PrintReceiptButtonStyled = styled(ButtonOutlineLarge)`
    display: flex;
    align-items: center;

    svg path {
        fill: ${colors.azure};
        transition: fill 0.2s ease-in-out;
    }

    &:hover {
        svg path {
            fill: ${colors.white};
        }
    }
`;

const OrderReceipt = ({ cart }) => {
    const { itemsGroupedByMember } = useCartItemsGroupedByMember({
        cart,
        itemCount: cart ? cart.items.length : null,
        total: cart?.total,
    });

    const orderReceipt = JSON.parse(window.localStorage.getItem('orderReceipt'));
    const appliedStoreCredit = orderReceipt?.appliedStoreCredit
        ? orderReceipt.appliedStoreCredit
        : cart._calculated?.appliedStoreCredit;
    const tax = orderReceipt?.tax ? orderReceipt.tax : cart._calculated?.tax;

    const orderDate = useMemo(() => {
        return new Date(cart.order.completed_at).toLocaleDateString();
    }, [cart]);

    if (!itemsGroupedByMember) {
        return null;
    }

    return (
        <div>
            <H4>Order Receipt</H4>
            <BodySmall>{orderDate}</BodySmall>
            <DividerLightGreyStyled className="mb-1rem mt-2.5rem" />

            {itemsGroupedByMember.map(memberGroup => (
                <CartSummaryMember key={memberGroup.memberId} isReceiptView {...memberGroup} />
            ))}

            <DividerLightGreyStyled className="mb-1rem mt-1.25rem" />

            {appliedStoreCredit > 0 && (
                <div className="flex justify-between">
                    <BodyMedium>Store Credit</BodyMedium>
                    <BodyMedium>{`-${formatMoney(appliedStoreCredit, cart.locale)}`}</BodyMedium>
                </div>
            )}

            {tax > 0 && (
                <div className="flex justify-between">
                    <BodyMedium>Tax</BodyMedium>
                    <BodyMedium>{formatMoney(tax, cart.locale)}</BodyMedium>
                </div>
            )}

            <div className="flex justify-between">
                <BodyMedium className="font-bold">Total</BodyMedium>
                <BodyMedium className="font-bold">{formatMoney(cart.total, cart.locale)}</BodyMedium>
            </div>

            <DividerLightGreyStyled className="mb-1.5rem mt-2.5rem" />

            <div>
                <BodyMedium>Payment Info</BodyMedium>
                <div className="flex items-center mt-1rem">
                    <CreditCardIcon />
                    <BodySmall className="ml-0.625rem">•••• •••• •••• {cart.order.credit_card_last_four}</BodySmall>
                </div>
            </div>


            <BodySmall className="mt-1.5rem mb-1rem">
                Please be advised that ICSC will only contact you through onPeak, ICSC's official hotel provider. Any
                other email or phone solicitations offering hotel bookings are unauthorized and should be disregarded as
                spam notices.
            </BodySmall>

            <BodySmall className="mb-1rem">
                Badge reprint fee applies.
            </BodySmall>

            <BodySmall className="mb-1rem">
                Read{' '}
                <LinkSmall href="https://www.icsc.com/terms-and-conditions#eventregistrants">
                    ICSC's Event Registration Terms and Conditions
                </LinkSmall>{' '}
                for more information.
            </BodySmall>

            <BodySmall className="mb-1rem">
                ICSC, 1251 Avenue of the Americas, New York, NY 10020
                <br />
                U.S./Canada Toll-Free Tel: +1 844 728 ICSC | Worldwide Tel: +1 646 728 3800
                <br />
                Email: <LinkSmall href="mailto:info@icsc.com">info@icsc.com</LinkSmall> |{' '}
                <LinkSmall href="https://www.icsc.com">www.icsc.com</LinkSmall>
            </BodySmall>

            <PrintReceiptContainerStyled>
                <PrintReceiptButtonStyled onClick={() => window.print()}>
                    <PrintIcon />
                    <span className="ml-0.625rem">Print Receipt</span>
                </PrintReceiptButtonStyled>
            </PrintReceiptContainerStyled>
        </div>
    );
};

OrderReceipt.propTypes = {
    cart: cartPropTypes.isRequired,
};

export default OrderReceipt;
